// React
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";

// @mui material components
import { Card, Checkbox, Fade, Icon, Modal, Tooltip, Grid, InputLabel } from "@mui/material";

import { gql, useLazyQuery } from "@apollo/client";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { formatMoneyPunto, formatDateARG, makeNiceText } from "utils/formatters";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ModalCargarPago from "./ModalPago";
import ModalHistorialPagos from "./ModalHistorialPagos";
import { UserContext } from "context/user";
import dayjs from "dayjs";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "92vh",
  width: { xs: "94%", sm: "90%", lg: "84%", xxl: "72%" },
  overflow: "hidden",
  borderRadius: 3,
};

const GET_REPORTE = gql`
  query reporteCuentaCorriente($fechaInicio: Date, $fechaFin: Date, $idSede: Int, $tipoCliente: TipoClienteCuentaCorriente) {
    reporteCuentaCorriente(
      fechaInicio: $fechaInicio
      fechaFin: $fechaFin
      idSede: $idSede
      tipoCliente: $tipoCliente
    ) {
      id
      nombre
      apellido
      dni
      saldo
      saldoActual
      saldoFechaCorte
      montoMaximoPagable
    }
  }
`;

const GET_PAGO_CC_CORTE_DISPONIBLE = gql`
  query pagoCuentaCorrienteCorteDisponible($idSede: Int, $tipoCliente: TipoClienteCuentaCorriente) {
    pagoCuentaCorrienteCorteDisponible(idSede: $idSede, tipoCliente: $tipoCliente) {
      fechaFinUltimoLote
      fechaMinimaCorte
      fechaMaximaCorte
      tieneFechaCorteDisponible
    }
  }
`;

const normalizeSearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const formatFechaCorteLabel = (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-");

const formatClienteNombre = (item) =>
  `${item?.nombre ? makeNiceText(item.nombre) : item?.nombre || ""} ${item?.apellido ? makeNiceText(item.apellido) : item?.apellido || ""
    }`.trim();

export default function ModalReporte({ open, handleClose }) {
  const [fechaFin, setFechaFin] = useState();
  const [searchCliente, setSearchCliente] = useState("");
  const [selectedClientesPagoIds, setSelectedClientesPagoIds] = useState([]);
  const [historialRefreshKey, setHistorialRefreshKey] = useState(0);
  const [getReporte, { data, loading }] = useLazyQuery(GET_REPORTE, {
    cachePolicy: "no-cache",
    fetchPolicy: "no-cache",
  });
  const [getCorteDisponible, { loading: loadingCorteDisponible }] = useLazyQuery(
    GET_PAGO_CC_CORTE_DISPONIBLE,
    {
      cachePolicy: "no-cache",
      fetchPolicy: "no-cache",
    }
  );
  const { user } = useContext(UserContext);
  const [corteDisponible, setCorteDisponible] = useState({
    fechaFinUltimoLote: null,
    fechaMinimaCorte: null,
    fechaMaximaCorte: dayjs().subtract(1, "day").format("YYYY-MM-DD"),
    tieneFechaCorteDisponible: true,
  });

  const [openModalPago, setOpenModalPago] = useState(false);
  const handleOpenModalPago = () => setOpenModalPago(true);
  const handleCloseModalPago = () => setOpenModalPago(false);
  const [openModalHistorial, setOpenModalHistorial] = useState(false);
  const handleOpenModalHistorial = () => setOpenModalHistorial(true);
  const handleCloseModalHistorial = () => setOpenModalHistorial(false);

  const reporteClientes = useMemo(() => data?.reporteCuentaCorriente || [], [data?.reporteCuentaCorriente]);
  const selectedClientesPagoIdsSet = useMemo(
    () => new Set(selectedClientesPagoIds),
    [selectedClientesPagoIds]
  );
  const reporteClientesFiltrados = useMemo(() => {
    const termino = normalizeSearch(searchCliente);
    if (!termino) return reporteClientes;
    return reporteClientes.filter((item) => {
      const nombreCompleto = normalizeSearch(`${item?.nombre || ""} ${item?.apellido || ""}`);
      const dni = String(item?.dni || "");
      return nombreCompleto.includes(termino) || dni.includes(termino);
    });
  }, [reporteClientes, searchCliente]);
  const selectedClientesPago = useMemo(
    () =>
      reporteClientes.filter((item) =>
        selectedClientesPagoIds.includes(Number(item.id))
      ),
    [reporteClientes, selectedClientesPagoIds]
  );
  const totalMaximoSeleccionado = useMemo(
    () =>
      selectedClientesPago.reduce(
        (acc, item) => acc + (Number(item.montoMaximoPagable) || 0),
        0
      ),
    [selectedClientesPago]
  );
  const selectedCountFiltrados = useMemo(
    () =>
      reporteClientesFiltrados.reduce(
        (acc, item) => acc + (selectedClientesPagoIdsSet.has(Number(item.id)) ? 1 : 0),
        0
      ),
    [reporteClientesFiltrados, selectedClientesPagoIdsSet]
  );
  const allSelectedPago =
    reporteClientesFiltrados.length > 0 &&
    selectedCountFiltrados === reporteClientesFiltrados.length;
  const hasSelectedPagoFiltrados = selectedCountFiltrados > 0;
  const hasSelectedPago = selectedClientesPagoIds.length > 0;
  const totalReporteSaldoActual = useMemo(
    () => reporteClientes.reduce((acc, item) => acc + (Number(item.saldoActual) || 0), 0),
    [reporteClientes]
  );
  const totalReporteSaldoFechaCorte = useMemo(
    () => reporteClientes.reduce((acc, item) => acc + (Number(item.saldoFechaCorte) || 0), 0),
    [reporteClientes]
  );
  const fechaFinFueraDeRango = useMemo(() => {
    if (!fechaFin) return false;
    if (
      corteDisponible?.fechaMinimaCorte &&
      dayjs(fechaFin).isBefore(dayjs(corteDisponible.fechaMinimaCorte), "day")
    ) {
      return true;
    }
    if (
      corteDisponible?.fechaMaximaCorte &&
      dayjs(fechaFin).isAfter(dayjs(corteDisponible.fechaMaximaCorte), "day")
    ) {
      return true;
    }
    return false;
  }, [fechaFin, corteDisponible]);
  const helperFechaCorte = useMemo(() => {
    if (loadingCorteDisponible) {
      return "Validando fecha disponible...";
    }
    if (!corteDisponible?.tieneFechaCorteDisponible) {
      return `No hay fechas de corte disponibles. Ultimo lote: ${formatFechaCorteLabel(corteDisponible?.fechaFinUltimoLote)} / maximo habilitado: ${formatFechaCorteLabel(corteDisponible?.fechaMaximaCorte)}.`;
    }
    if (corteDisponible?.fechaMinimaCorte) {
      return `Disponible desde ${formatFechaCorteLabel(corteDisponible.fechaMinimaCorte)} hasta ${formatFechaCorteLabel(corteDisponible?.fechaMaximaCorte)}.`;
    }
    return `Disponible hasta ${formatFechaCorteLabel(corteDisponible?.fechaMaximaCorte)}.`;
  }, [corteDisponible, loadingCorteDisponible]);
  const reporteDisponible =
    !fechaFinFueraDeRango &&
    Boolean(data && Array.isArray(data.reporteCuentaCorriente) && data.reporteCuentaCorriente.length > 0);

  useEffect(() => {
    if (!reporteClientes.length) {
      setSelectedClientesPagoIds((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    setSelectedClientesPagoIds((prev) =>
      prev.filter((id) =>
        reporteClientes.some((item) => Number(item.id) === Number(id))
      )
    );
  }, [reporteClientes]);

  useEffect(() => {
    if (!open) return;

    getCorteDisponible({
      variables: {
        idSede: parseInt(user?.sede?.id, 10) || null,
        tipoCliente: "NO_FUNCIONARIO",
      },
      onCompleted: (response) => {
        setCorteDisponible({
          fechaFinUltimoLote:
            response?.pagoCuentaCorrienteCorteDisponible?.fechaFinUltimoLote || null,
          fechaMinimaCorte:
            response?.pagoCuentaCorrienteCorteDisponible?.fechaMinimaCorte || null,
          fechaMaximaCorte:
            response?.pagoCuentaCorrienteCorteDisponible?.fechaMaximaCorte ||
            dayjs().subtract(1, "day").format("YYYY-MM-DD"),
          tieneFechaCorteDisponible:
            response?.pagoCuentaCorrienteCorteDisponible?.tieneFechaCorteDisponible ?? true,
        });
      },
      onError: () => {
        setCorteDisponible({
          fechaFinUltimoLote: null,
          fechaMinimaCorte: null,
          fechaMaximaCorte: dayjs().subtract(1, "day").format("YYYY-MM-DD"),
          tieneFechaCorteDisponible: false,
        });
      },
    });
  }, [open, user?.sede?.id, historialRefreshKey, getCorteDisponible]);

  const toggleClientePagoSelection = (idCliente) => {
    setSelectedClientesPagoIds((prev) => {
      const parsedId = Number(idCliente);
      if (prev.includes(parsedId)) {
        return prev.filter((id) => id !== parsedId);
      }
      return [...prev, parsedId];
    });
  };

  const toggleAllClientesPagoSelection = () => {
    const idsFiltrados = reporteClientesFiltrados.map((item) => Number(item.id));
    const idsFiltradosSet = new Set(idsFiltrados);
    if (allSelectedPago) {
      setSelectedClientesPagoIds((prev) => prev.filter((id) => !idsFiltradosSet.has(id)));
      return;
    }
    setSelectedClientesPagoIds((prev) => [...new Set([...prev, ...idsFiltrados])]);
  };

  const exportToExcel = () => {
    if (!reporteClientes.length) return;

    const encabezado = [
      ["Reporte deuda No funcionario"],
      ["Fecha de generacion", dayjs().format("YYYY-MM-DD HH:mm:ss")],
      ["Fecha de corte", fechaFin || ""],
      ["Cantidad de clientes", reporteClientes.length],
      ["Saldo actual total", totalReporteSaldoActual],
      ["Saldo a fecha de corte total", totalReporteSaldoFechaCorte],
      [],
      ["Cliente", "DNI", "Saldo actual", "Saldo a fecha de corte", "Maximo pagable"],
    ];

    const cuerpo = reporteClientes.map((item) => ([
      `${item.nombre || ""} ${item.apellido || ""}`.trim(),
      item.dni || "",
      Number(item.saldoActual) || 0,
      Number(item.saldoFechaCorte) || 0,
      Number(item.montoMaximoPagable) || 0,
    ]));

    const ws = XLSX.utils.aoa_to_sheet(encabezado);
    XLSX.utils.sheet_add_aoa(ws, cuerpo, { origin: "A9" });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reporte deuda");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = `Reporte_Deuda_No_Funcionario_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.xlsx`;
    saveAs(blob, fileName);
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox
            px={3}
            py={2.5}
            sx={{
              borderBottom: "1px solid #E7EDF5",
              background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
            }}
          >
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftBox>
                <SoftTypography variant="h5" fontWeight="bold">
                  Reporte de cuenta corriente
                </SoftTypography>
                <SoftTypography variant="button" color="text">
                  Selecciona clientes para registrar pagos parciales.
                </SoftTypography>
              </SoftBox>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer", color: "#334E72" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
          </SoftBox>

          <SoftBox
            p={3}
            sx={{
              position: "relative",
              maxHeight: "calc(92vh - 100px)",
              overflowY: "auto",
              backgroundColor: "#F4F7FB",
            }}
          >
            <SoftBox
              p={2}
              mb={2}
              sx={{
                backgroundColor: "white",
                borderRadius: 2,
                border: "1px solid #E1E8F0",
              }}
            >
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={6} lg={3}>
                  <InputLabel htmlFor="fechaFin">
                    <SoftTypography variant="caption" fontWeight="bold">Fecha de corte</SoftTypography>
                  </InputLabel>
                  <SoftInput
                    type="date"
                    id="fechaFin"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    inputProps={{
                      min: corteDisponible?.fechaMinimaCorte || undefined,
                      max: corteDisponible?.fechaMaximaCorte || undefined,
                    }}
                    error={fechaFinFueraDeRango}
                    disabled={loadingCorteDisponible || !corteDisponible?.tieneFechaCorteDisponible}
                  />
                  <SoftTypography
                    variant="caption"
                    color={fechaFinFueraDeRango || !corteDisponible?.tieneFechaCorteDisponible ? "error" : "text"}
                    display="block"
                    mt={0.5}
                  >
                    {helperFechaCorte}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} lg={9}>
                  <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent={{ xs: "flex-start", lg: "flex-end" }}>
                    <SoftButton
                      color="primary"
                      onClick={() => {
                        getReporte({
                          variables: {
                            fechaFin,
                            idSede: parseInt(user?.sede?.id, 10) || null,
                            tipoCliente: "NO_FUNCIONARIO",
                          },
                        });
                        setSelectedClientesPagoIds([]);
                        setSearchCliente("");
                      }}
                      disabled={
                        !fechaFin ||
                        fechaFinFueraDeRango ||
                        loadingCorteDisponible ||
                        !corteDisponible?.tieneFechaCorteDisponible
                      }
                    >
                      <Icon sx={{ mr: 0.75 }}>search</Icon>
                      Generar
                    </SoftButton>
                    <SoftButton
                      color="success"
                      onClick={handleOpenModalPago}
                      disabled={loading || !reporteDisponible || !hasSelectedPago}
                    >
                      <Icon sx={{ mr: 0.75 }}>send</Icon>
                      Trasladar a Upro
                    </SoftButton>
                    <Tooltip title="Descargar Excel" placement="top">
                      <SoftButton
                        color="info"
                        iconOnly
                        circular
                        onClick={exportToExcel}
                        disabled={loading || !reporteDisponible}
                      >
                        <Icon>download</Icon>
                      </SoftButton>
                    </Tooltip>
                    <Tooltip title="Todos los registros trasladados a Upro" placement="top">
                      <SoftButton
                        color="success"
                        iconOnly
                        circular
                        onClick={handleOpenModalHistorial}
                      >
                        <Icon>history</Icon>
                      </SoftButton>
                    </Tooltip>
                  </SoftBox>
                </Grid>
              </Grid>
            </SoftBox>

            <SoftBox sx={{ position: "relative", minHeight: "48vh" }}>
              {loading ? (
                <SoftBox
                  sx={{
                    minHeight: "40vh",
                    backgroundColor: "white",
                    borderRadius: 2,
                    border: "1px solid #E1E8F0",
                  }}
                >
                  <Loading />
                </SoftBox>
              ) : reporteDisponible ? (
                <>
                  <SoftBox
                    p={1.5}
                    sx={{
                      border: "1px solid #E1E8F0",
                      borderRadius: 2,
                      backgroundColor: "white",
                    }}
                  >
                    <SoftBox px={1} pb={1}>
                      <SoftInput
                        placeholder="Buscar por nombre o DNI"
                        value={searchCliente}
                        onChange={(e) => setSearchCliente(e.target.value)}
                      />
                      <SoftTypography variant="caption" color="text" px={0.25}>
                        Mostrando {reporteClientesFiltrados.length} de {reporteClientes.length} clientes
                      </SoftTypography>
                    </SoftBox>
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      sx={{
                        px: 1,
                        py: 1,
                        borderBottom: "1px solid #E9EFF6",
                        minWidth: 760,
                      }}
                    >
                      <SoftBox sx={{ width: 42, display: "flex", justifyContent: "center" }}>
                        <Checkbox
                          checked={allSelectedPago}
                          indeterminate={hasSelectedPagoFiltrados && !allSelectedPago}
                          onChange={toggleAllClientesPagoSelection}
                          size="small"
                        />
                      </SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ flex: 1 }}>
                        CLIENTE
                      </SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ width: 120, textAlign: "center" }}>
                        DNI
                      </SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ width: 170, textAlign: "right", pr: 1 }}>
                        SALDO ACTUAL
                      </SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ width: 190, textAlign: "right", pr: 1 }}>
                        SALDO FECHA CORTE
                      </SoftTypography>
                    </SoftBox>

                    <SoftBox sx={{ maxHeight: "42vh", overflowX: "auto", overflowY: "auto", pr: 0.5 }}>
                      {reporteClientesFiltrados.map((item) => {
                        const isChecked = selectedClientesPagoIds.includes(Number(item.id));
                        return (
                          <SoftBox
                            key={item.id}
                            display="flex"
                            alignItems="center"
                            sx={{
                              mt: 1,
                              px: 1,
                              py: 1,
                              borderRadius: 1.5,
                              border: "1px solid",
                              borderColor: isChecked ? "#9CC2F8" : "#E6EDF4",
                              backgroundColor: isChecked ? "#F3F8FF" : "white",
                              transition: "all .15s ease",
                              minWidth: 760,
                              "&:hover": {
                                backgroundColor: isChecked ? "#F3F8FF" : "#F8FAFD",
                              },
                            }}
                          >
                            <SoftBox sx={{ width: 42, display: "flex", justifyContent: "center" }}>
                              <Checkbox
                                checked={isChecked}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleClientePagoSelection(item.id)}
                                size="small"
                              />
                            </SoftBox>
                            <SoftTypography variant="button" sx={{ flex: 1, pr: 1 }}>
                              {formatClienteNombre(item)}
                            </SoftTypography>
                            <SoftTypography variant="button" sx={{ width: 120, textAlign: "center" }}>
                              {item.dni}
                            </SoftTypography>
                            <SoftTypography variant="button" fontWeight="bold" sx={{ width: 170, textAlign: "right", pr: 1 }}>
                              $ {formatMoneyPunto(item.saldoActual || 0)}
                            </SoftTypography>
                            <SoftTypography variant="button" fontWeight="bold" sx={{ width: 190, textAlign: "right", pr: 1 }}>
                              $ {formatMoneyPunto(item.saldoFechaCorte || 0)}
                            </SoftTypography>
                          </SoftBox>
                        );
                      })}
                      {!reporteClientesFiltrados.length && (
                        <SoftBox py={3}>
                          <SinDatos titulo="Sin coincidencias" mensaje="Intente con otro nombre o DNI" />
                        </SoftBox>
                      )}
                    </SoftBox>
                  </SoftBox>

                  <SoftBox
                    mt={2}
                    p={2}
                    sx={{
                      border: "1px solid #DCE6F2",
                      borderRadius: 2,
                      backgroundColor: "white",
                    }}
                  >
                    <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                      <Grid item xs={12} md={3}>
                        <SoftTypography variant="button" color="text">
                          Clientes seleccionados: {selectedClientesPagoIds.length}
                        </SoftTypography>
                      </Grid>
                      {/* <Grid item xs={12} md={3}>
                        <SoftTypography variant="button" color="text">
                          Total saldo actual: $ {formatMoneyPunto(totalReporteSaldoActual || 0)}
                        </SoftTypography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <SoftTypography variant="button" color="text">
                          Total saldo fecha corte: $ {formatMoneyPunto(totalReporteSaldoFechaCorte || 0)}
                        </SoftTypography>
                      </Grid> */}
                      <Grid item xs={12} md={3} sx={{ textAlign: { xs: "left", md: "right" } }}>
                        <SoftTypography variant="h6" fontWeight="bold">
                          Total maximo seleccionable: $ {formatMoneyPunto(totalMaximoSeleccionado || 0)}
                        </SoftTypography>
                      </Grid>
                    </Grid>
                  </SoftBox>
                </>
              ) : (
                <SoftBox
                  sx={{
                    minHeight: "40vh",
                    backgroundColor: "white",
                    borderRadius: 2,
                    border: "1px solid #E1E8F0",
                    p: 2,
                  }}
                >
                  <SinDatos />
                </SoftBox>
              )}
            </SoftBox>
          </SoftBox>

          <ModalCargarPago
            open={openModalPago}
            handleClose={handleCloseModalPago}
            getReporte={() =>
              getReporte({
                variables: {
                  fechaFin,
                  idSede: parseInt(user?.sede?.id, 10) || null,
                  tipoCliente: "NO_FUNCIONARIO",
                },
              })
            }
            fechaFin={fechaFin}
            idSede={parseInt(user?.sede?.id, 10) || null}
            clientes={selectedClientesPago}
            onPagoRegistrado={() => setHistorialRefreshKey((prev) => prev + 1)}
          />
          <ModalHistorialPagos
            open={openModalHistorial}
            handleClose={handleCloseModalHistorial}
            idSede={parseInt(user?.sede?.id, 10) || null}
            tipoCliente="NO_FUNCIONARIO"
            refreshKey={historialRefreshKey}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalReporte.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};
